import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useRef} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useLinkNavigation} from '../../../../components/common/navigation/useLinkNavigation';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {IntegrationModel} from '../../../../models/api/integrationModel';
import {IntegrationTypeModel} from '../../../../models/api/integrationTypesModel';
import {buildSettingsAppsTabUrl} from '../../../../models/url/settingsUrlHelpers';
import {
  SettingsAppsAppDetailsTabsEnum,
  SettingsAppsAppDetailsUrl,
  SettingsAppsTabsEnum,
} from '../../../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {maybeFetchIntegrationTypeDescriptionAsyncAction} from '../../../../store/data/integrationTypes/integrationTypesActions';
import {SettingsAppsDetailsGettingStartedPageContainer} from './settingsAppsDetailsGettingStartedPageContainer';
import {SettingsAppsDetailsOverviewPage} from './settingsAppsDetailsOverviewPage';
import {SettingsAppsDetailsSidepanelContainer} from './settingsAppsDetailsSidepanelContainer';

/*
 * Props.
 */

interface SettingsAppsDetailsPageProps {
  url: SettingsAppsAppDetailsUrl;
  integrationType?: IntegrationTypeModel;
  integration?: IntegrationModel;
  isIntegrationTypesLoading: boolean;
}

/*
 * Style.
 */

const StyledDetailsPageWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  font-weight: ${fontWeights.bold};
  gap: 16px;
`;

const StyledEmptyStateWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  padding: 64px;
  gap: 32px;
  text-align: center;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  align-self: center;
`;

const StyledContentWrapperDiv = styled.div`
  flex: 1;
`;

const StyledSidepanelWrapperDiv = styled.div`
  flex: 0 0 248px;
  position: relative;
`;

/*
 * Component.
 */

export const SettingsAppsDetailsPage: FC<SettingsAppsDetailsPageProps> = (props) => {
  const {url, integrationType, integration, isIntegrationTypesLoading} = props;
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();
  const hasTrackedRef = useRef(false);

  const isViewingOverviewTab = url.detailsPage === SettingsAppsAppDetailsTabsEnum.OVERVIEW;
  const isViewingGettingStartedTab = url.detailsPage === SettingsAppsAppDetailsTabsEnum.GETTING_STARTED;

  useEffect(() => {
    if (integrationType?.id) {
      dispatch(maybeFetchIntegrationTypeDescriptionAsyncAction(integrationType.id));
    }
  }, [integrationType?.id, dispatch]);

  useEffect(() => {
    // If we are loading or already have tracked this integration view.
    if (isIntegrationTypesLoading || hasTrackedRef.current) {
      return;
    }
    hasTrackedRef.current = true;
    trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_PAGE_VIEW, {
      settings_page: 'integration details',
      integration_id: integration?.id,
      integration_type: integrationType?.id,
    });
  }, [integration?.id, integrationType?.id, isIntegrationTypesLoading]);

  if (isIntegrationTypesLoading) {
    return <SettingsAppsDetailsOverviewPage isLoading isIntegrationTypesLoading />;
  }

  // If we are not loading and we failed to find an integration type we should render the empty state.
  if (!integrationType) {
    return renderEmptyState(url, navigateToLink);
  }

  return (
    <StyledDetailsPageWrapperDiv>
      <StyledContentWrapperDiv>
        {isViewingOverviewTab && <SettingsAppsDetailsOverviewPage integrationType={integrationType} />}
        {isViewingGettingStartedTab && (
          <SettingsAppsDetailsGettingStartedPageContainer integrationType={integrationType} />
        )}
      </StyledContentWrapperDiv>
      <StyledSidepanelWrapperDiv>
        <SettingsAppsDetailsSidepanelContainer integrationType={integrationType} url={url} />
      </StyledSidepanelWrapperDiv>
    </StyledDetailsPageWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderEmptyState(url: SettingsAppsAppDetailsUrl, navigateToLink: HandlerOf<string>) {
  const onButtonClick = () => {
    if (url.previousUrl) {
      return navigateToLink(url.previousUrl);
    }
    return navigateToLink(buildSettingsAppsTabUrl(SettingsAppsTabsEnum.COLLECTIONS));
  };

  return (
    <StyledEmptyStateWrapperDiv>
      <Trans id="settings-apps-details-empty-state">We are not able to find this app.</Trans>
      <StyledButtonWrapperDiv>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onButtonClick}>
          <Trans id="settings-apps-details-empty-state-button">Back</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </StyledEmptyStateWrapperDiv>
  );
}
